// Definição do menu lateral do admin — fonte ÚNICA de verdade de itens/rótulos/permissões, consumida por
// `AdminLayout` (render + gate de visibilidade) e por `adminTabs/adminTabTitle` (título das abas).
import type { ComponentType } from "react";
import {
  FileCheck, FileText, Users, Tag, ShieldCheck, Package, LayoutDashboard, Workflow, UserCog,
  Truck, Building2, Warehouse, ClipboardList, BarChart2, ShoppingCart, Boxes, Settings, FilePlus,
  Route, Wrench, GraduationCap, Wallet, BarChart3, Coins, Box, Search, Tags, LifeBuoy, BookOpen,
  PenSquare,
} from "lucide-react";

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
  /** Permissões que liberam o item (acesso individual). Visível se a conta tiver AO MENOS UMA. */
  anyOf: string[];
  /** EXCLUSIVO do cargo ADMIN: nenhuma permissão concedida a outro cargo libera o item (espelha o backend). */
  adminOnly?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  items: NavItem[];
};

export type NavEntry = NavItem | NavGroup;

export function isGroup(entry: NavEntry): entry is NavGroup {
  return (entry as NavGroup).items !== undefined;
}

export const NAV: NavEntry[] = [
  {
    id: "dashboards",
    label: "Dashboard",
    icon: LayoutDashboard,
    items: [
      { to: "/dashboard/operacoes", label: "Operações", icon: Workflow, anyOf: ["PEDIDOS.PEDIDO.VISUALIZAR"] },
      { to: "/dashboard/compras", label: "Compras", icon: ShoppingCart, anyOf: ["PEDIDOS.COMPRA.AVANCAR"] },
      { to: "/dashboard/producao", label: "Produção", icon: ClipboardList, anyOf: ["PEDIDOS.PRODUCAO.AVANCAR", "PEDIDOS.PRODUCAO.IN_NATURA"] },
      { to: "/dashboard/expedicao", label: "Expedição", icon: Truck, anyOf: ["PEDIDOS.EXPEDICAO.AVANCAR"] },
    ],
  },
  { to: "/admin/operacoes", label: "Operações", icon: Workflow, anyOf: ["PEDIDOS.PEDIDO.VISUALIZAR"] },
  { to: "/admin/selecao", label: "Processo Seletivo", icon: GraduationCap, anyOf: ["SELECAO.PROCESSO.VISUALIZAR"] },
  {
    id: "logistica",
    label: "Logística",
    icon: Route,
    items: [
      { to: "/admin/entregas", label: "Entregas", icon: Truck, anyOf: ["ENTREGAS.ROTA.VISUALIZAR"] },
    ],
  },
  {
    id: "comercial",
    label: "Comercial",
    icon: ShoppingCart,
    items: [
      { to: "/admin/pedidos/novo", label: "Novo pedido manual", icon: FilePlus, anyOf: ["PEDIDOS.PEDIDO.CRIAR_MANUAL"] },
      { to: "/admin/clientes", label: "Clientes", icon: Users, anyOf: ["CLIENTES.CLIENTE.VISUALIZAR"] },
      { to: "/admin/unidades-entrega", label: "Unidades de entrega", icon: Building2, anyOf: ["CLIENTES.UNIDADE.VISUALIZAR"] },
    ],
  },
  {
    id: "financeiro",
    label: "Financeiro",
    icon: Wallet,
    items: [
      { to: "/admin/financeiro/contas-a-receber", label: "Contas a Receber", icon: Wallet, anyOf: ["FINANCEIRO.CONTAS_RECEBER.VISUALIZAR"] },
      { to: "/admin/financeiro/resultados", label: "Painel de Resultados", icon: BarChart3, anyOf: ["FINANCEIRO.RESULTADOS.VISUALIZAR"] },
    ],
  },
  {
    // Menu principal "Notas Fiscais". Hoje só "Notas de entrada" (NF de fornecedor); o prefixo técnico da
    // permissão continua FINANCEIRO.NF_ENTRADA.* (sem reconversão) — mover de grupo não concede acesso, não
    // muda a rota /admin/nf-entrada e preserva o gate de feature flag (supplierNfEnabled).
    id: "notas-fiscais",
    label: "Notas Fiscais",
    icon: FileText,
    items: [
      { to: "/admin/estabelecimentos", label: "Estabelecimentos", icon: Building2, anyOf: ["FINANCEIRO.ESTABELECIMENTO.VISUALIZAR"] },
      { to: "/admin/nf-entrada", label: "Notas de entrada", icon: FileCheck, anyOf: ["FINANCEIRO.NF_ENTRADA.VISUALIZAR"] },
      { to: "/admin/buscador-nfe", label: "Buscador de NF-e", icon: Search, anyOf: [], adminOnly: true },
      { to: "/admin/certificados", label: "Certificado digital", icon: ShieldCheck, anyOf: ["FINANCEIRO.CERTIFICADO.VISUALIZAR"] },
    ],
  },
  {
    id: "catalogo",
    label: "Catálogo",
    icon: Package,
    items: [
      { to: "/admin/produtos", label: "Produtos", icon: Package, anyOf: ["CATALOGO.PRODUTO.VISUALIZAR"] },
      { to: "/admin/custos", label: "Custos dos produtos", icon: Coins, anyOf: ["CATALOGO.CUSTO.VISUALIZAR"] },
      { to: "/admin/tabelas", label: "Tabelas de preço", icon: Tag, anyOf: ["PRECOS.TABELA.VISUALIZAR"] },
    ],
  },
  {
    id: "frota",
    label: "Frota",
    icon: Wrench,
    items: [
      { to: "/admin/frota", label: "Manutenção de veículos", icon: Wrench, anyOf: ["FROTA.VEICULO.VISUALIZAR"] },
    ],
  },
  {
    id: "suprimentos",
    label: "Suprimentos",
    icon: Boxes,
    items: [
      { to: "/admin/estoque", label: "Estoque", icon: Warehouse, anyOf: ["SUPRIMENTOS.ESTOQUE.VISUALIZAR"] },
      { to: "/admin/embalagens", label: "Embalagens", icon: Box, anyOf: ["SUPRIMENTOS.EMBALAGEM.VISUALIZAR"] },
      { to: "/admin/fornecedores", label: "Fornecedores", icon: Building2, anyOf: ["SUPRIMENTOS.FORNECEDOR.VISUALIZAR"] },
      { to: "/admin/recebimentos", label: "Recebimentos", icon: ClipboardList, anyOf: ["SUPRIMENTOS.RECEBIMENTO.VISUALIZAR"] },
    ],
  },
  {
    id: "controle-caixas",
    label: "Controle de Caixas",
    icon: Boxes,
    items: [
      { to: "/admin/caixas", label: "Caixas", icon: Box, end: true, anyOf: ["CAIXAS.MOVIMENTACAO.VISUALIZAR"] },
      { to: "/admin/caixas/materiais", label: "Materiais e tipos", icon: Tags, anyOf: [], adminOnly: true },
      { to: "/admin/caixas/precos", label: "Preços de caixas", icon: Coins, anyOf: [], adminOnly: true },
    ],
  },
  {
    id: "relatorios",
    label: "Relatórios",
    icon: BarChart2,
    items: [
      { to: "/admin/relatorios/vendas", label: "Vendas", icon: BarChart2, anyOf: ["RELATORIOS.VENDAS.VISUALIZAR"] },
      { to: "/admin/relatorios/produtos", label: "Produtos", icon: Package, anyOf: ["RELATORIOS.PRODUTOS.VISUALIZAR"] },
      { to: "/admin/relatorios/pedidos", label: "Pedidos", icon: ClipboardList, anyOf: ["RELATORIOS.PEDIDOS.VISUALIZAR"] },
      { to: "/admin/relatorios/producao", label: "Produção", icon: ClipboardList, anyOf: ["RELATORIOS.PRODUCAO.VISUALIZAR"] },
      { to: "/admin/relatorios/lista-de-compras", label: "Lista de Compras", icon: ShoppingCart, anyOf: ["RELATORIOS.LISTA_COMPRAS.VISUALIZAR"] },
      { to: "/admin/relatorios/precificacao", label: "Precificação", icon: Tag, anyOf: ["RELATORIOS.PRECIFICACAO.VISUALIZAR"] },
      { to: "/admin/relatorios/rentabilidade", label: "Rentabilidade", icon: BarChart3, anyOf: ["RELATORIOS.RENTABILIDADE.VISUALIZAR"] },
      { to: "/admin/relatorios/recebimentos", label: "Recebimentos", icon: Truck, anyOf: ["RELATORIOS.RECEBIMENTOS.VISUALIZAR"] },
    ],
  },
  {
    id: "ajuda",
    label: "Ajuda e Treinamento",
    icon: LifeBuoy,
    items: [
      { to: "/admin/ajuda", label: "Central de Ajuda", icon: BookOpen, end: true, anyOf: ["AJUDA.CENTRAL.VISUALIZAR"] },
      { to: "/admin/ajuda/gerenciar", label: "Gerenciar artigos", icon: PenSquare, anyOf: [], adminOnly: true },
    ],
  },
  {
    id: "config",
    label: "Configurações",
    icon: Settings,
    items: [
      { to: "/admin/funcionarios", label: "Funcionários", icon: UserCog, anyOf: ["STAFF.FUNCIONARIO.VISUALIZAR"] },
      { to: "/admin/permissoes", label: "Permissões", icon: ShieldCheck, anyOf: ["STAFF.FUNCIONARIO.VISUALIZAR"] },
      { to: "/admin/configuracoes/entregas", label: "Entregas", icon: Truck, anyOf: ["ENTREGAS.CONFIG.VISUALIZAR"] },
    ],
  },
];

/** Item liberado quando a conta tem AO MENOS UMA das permissões do item (sem bypass por cargo). */
export function itemAllowed(item: NavItem, permissions: readonly string[]): boolean {
  if (item.adminOnly) return false;
  return item.anyOf.some((p) => permissions.includes(p));
}

export function filterItems(items: NavItem[], permissions: readonly string[]): NavItem[] {
  return items.filter((it) => itemAllowed(it, permissions));
}

/** Itens só visíveis com o módulo "NF de fornecedor" ligado (feature flag do backend), além da permissão. */
const FLAG_GATED_PATHS = new Set([
  "/admin/nf-entrada",
  "/admin/certificados",
  "/admin/estabelecimentos",
  "/admin/buscador-nfe",
]);

/**
 * Navegação visível para a conta — fonte ÚNICA consumida pela sidebar (AdminLayout) e pela página de
 * boas-vindas (atalhos). Regras: ADMIN enxerga TUDO; demais, só itens com permissão; itens sob feature flag
 * só aparecem com `supplierNfEnabled`. Mantém os grupos (com seus itens já filtrados) e os itens avulsos.
 */
export function buildVisibleNav(
  account: { role?: string; permissions?: string[] } | null | undefined,
  supplierNfEnabled: boolean,
): NavEntry[] {
  const permissions = account?.permissions ?? [];
  const isAdmin = account?.role === "ADMIN";
  const flagAllowed = (item: NavItem) => (FLAG_GATED_PATHS.has(item.to) ? supplierNfEnabled : true);
  const out: NavEntry[] = [];
  for (const entry of NAV) {
    if (isGroup(entry)) {
      const items = (isAdmin ? entry.items : filterItems(entry.items, permissions)).filter(flagAllowed);
      if (items.length > 0) out.push({ ...entry, items });
    } else if ((isAdmin || itemAllowed(entry, permissions)) && flagAllowed(entry)) {
      out.push(entry);
    }
  }
  return out;
}
